import type { ReactNode } from 'react';
import type { FieldValues, Path } from 'react-hook-form';
import { useController } from 'react-hook-form';

import type { ComponentProps } from '~/chords/ComponentProps';
import { FieldMessage } from '~/chords/FieldMessage';
import { useFieldIsRequired } from '~/chords/Form';
import { getClassNameOverride } from '~/chords/getClassNameOverride';
import { Label } from '~/chords/Label';
import { cn } from '~/chords/lib/cn';

import { assertNonEmptyName } from './assertions/nonEmptyName';

export type FormFieldProps<TFieldValues extends FieldValues> = ComponentProps<{
  name: Path<TFieldValues>;
  label?: string;
  disabled?: boolean;
  readOnly?: boolean;
  triggerClassName?: string;
  showErrorMessage?: boolean;
  fieldMessageClassName?: string;
  children: ReactNode;

  /**
   * Disables the label HTML tag and uses span instead.
   * Useful when the inner content contains its own labels or multiple inputs.
   *
   * @default false
   */
  labelAsSpan?: boolean;

  /**
   * Optional addon to render inline with the label (e.g., info icon).
   */
  labelAddon?: ReactNode;
}>;

export function FormField<TFieldValues extends FieldValues>({
  name,
  label,
  disabled,
  readOnly,
  children,
  labelAsSpan,
  labelAddon,
  fieldMessageClassName,
  showErrorMessage = true,
  ...props
}: FormFieldProps<TFieldValues>) {
  assertNonEmptyName(name);

  const {
    fieldState: { error },
  } = useController<TFieldValues>({ name, disabled });

  const isRequired = useFieldIsRequired(name);
  const className = getClassNameOverride(props);

  return (
    <div className={cn('flex flex-col gap-sm', className)}>
      {label ? (
        <Label
          htmlFor={name}
          disabled={disabled || readOnly}
          required={isRequired}
          // eslint-disable-next-line @typescript-eslint/no-deprecated
          asSpan={labelAsSpan}
          name={name}
        >
          {label}
          {labelAddon}
        </Label>
      ) : null}
      {children}
      {error && showErrorMessage ? (
        <FieldMessage
          state="invalid"
          dangerouslyOverrideClassName={{ __className: fieldMessageClassName }}
        >
          {error.message}
        </FieldMessage>
      ) : null}
    </div>
  );
}

FormField.displayName = 'FormField';
